import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Lock, LogOut, RefreshCw, Trash2, Users, Mail, Smartphone, ShieldCheck, Flame, Download, Newspaper, MousePointerClick, Radar, LayoutDashboard, Columns3, Search, BarChart3, Star, Plug, FileSpreadsheet, ExternalLink } from "lucide-react";
import Seo from "../components/Seo";
import { useToast } from "../hooks/use-toast";
import Overview from "../components/admin/Overview";
import Pipeline from "../components/admin/Pipeline";
import SeoSearch from "../components/admin/SeoSearch";
import Analytics from "../components/admin/Analytics";
import GbpReviews from "../components/admin/GbpReviews";
import Connections from "../components/admin/Connections";
import { RangeToggle } from "../components/admin/shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch (e) {
    return iso;
  }
};

const MARKETING_TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "pipeline", label: "Lead Pipeline", icon: Columns3 },
  { id: "seo", label: "SEO & Search", icon: Search },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
  { id: "gbp", label: "GBP & Reviews", icon: Star },
  { id: "connections", label: "Connections", icon: Plug },
];

const TABS = [
  { id: "leads", label: "Goal Review Leads", icon: Users },
  { id: "contacts", label: "Contact Messages", icon: Mail },
  { id: "waitlist", label: "App Waitlist", icon: Smartphone },
  { id: "challenge", label: "8-Week Challenge", icon: Flame },
  { id: "subscribers", label: "Grit Report", icon: Newspaper },
  { id: "clicks", label: "ChalkIt Clicks", icon: MousePointerClick },
];

const PRODUCT_LABELS = {
  goalReview: "Free Goal Review (Booking)",
  unlimited: "Platinum Membership ($275)",
  "3x": "3x/Week Membership ($225)",
  big365: "Live B.I.G. 365 ($100)",
  punch10: "Punch Card · 10 ($300)",
  punch30: "Punch Card · 30 ($750)",
  punch52: "Punch Card · 52 ($1,040)",
  pt5: "Personal Training · 5 ($750)",
  pt10: "Personal Training · 10 ($1,250)",
  fundamentals: "Fundamentals ($625)",
  hyroxAddOn: "HYROX Add-On ($50/mo)",
  hyroxPunchMembers: "HYROX Punch · Members ($480)",
  hyroxPunch24: "HYROX Punch · New Athletes ($600)",
  dropIn: "Day Drop-In ($35)",
  dropInWeek: "Week Pass ($75)",
  labsMen: "Men's Labs",
  labsWomen: "Women's Labs",
  goldFoundation: "Gold Standard · Foundation ($1,985)",
  goldAscension: "Gold Standard · Ascension",
  goldElite: "Gold Standard · Elite",
  general_booking: "General Booking",
  member_reserve: "Member Reserve (app login)",
  unknown: "Other",
};

const CSV_CONFIGS = {
  leads: {
    name: "website-leads",
    cols: [["First Name", "first_name"], ["Last Name", "last_name"], ["Email", "email"], ["Phone", "phone"], ["Program", "program_interest"], ["Goal", "primary_goal"], ["Experience", "experience_level"], ["Contact Via", "preferred_contact_method"], ["Preferred Day", "preferred_day"], ["Preferred Time", "preferred_time"], ["Message", "message"], ["Form", "form_name"], ["Page", "page_url"], ["UTM Source", "utm_source"], ["UTM Medium", "utm_medium"], ["UTM Campaign", "utm_campaign"], ["GCLID", "gclid"], ["Consent", "marketing_consent"], ["Synced", "chalkit_synced"], ["Received", "created_at"]],
  },
  contacts: {
    name: "contact-messages",
    cols: [["Name", "name"], ["Email", "email"], ["Phone", "phone"], ["Message", "message"], ["Received", "created_at"]],
  },
  waitlist: {
    name: "app-waitlist",
    cols: [["Email", "email"], ["Name", "name"], ["Source", "source"], ["Joined", "created_at"]],
  },
  challenge: {
    name: "challenge-signups",
    cols: [["Name", "name"], ["Email", "email"], ["Phone", "phone"], ["Joined", "created_at"]],
  },
  subscribers: {
    name: "grit-report-subscribers",
    cols: [["Email", "email"], ["First Name", "first_name"], ["Last Name", "last_name"], ["Source", "source"], ["Status", "status"], ["Joined", "created_at"]],
  },
  clicks: {
    name: "chalkit-clicks",
    cols: [["Product", "label"], ["Last 7 Days", "last7"], ["Last 30 Days", "last30"], ["Total Clicks", "total"], ["Last Click", "last_click"]],
  },
};

export default function Admin() {
  const { toast } = useToast();
  const [key, setKey] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [legacyMode, setLegacyMode] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("overview");
  const [range, setRange] = useState("28d");
  const [refreshKey, setRefreshKey] = useState(0);
  const [settings, setSettings] = useState(null);
  const [data, setData] = useState({ leads: [], contacts: [], waitlist: [], challenge: [], subscribers: [], clicks: [] });

  const headers = useCallback(
    (k) => ({ headers: { "X-Admin-Key": k || sessionStorage.getItem("ig_admin_token") || "" } }),
    []
  );

  const fetchAll = useCallback(
    async (k) => {
      setLoading(true);
      axios.get(`${API}/admin/settings`, headers(k)).then((r) => setSettings(r.data)).catch(() => {});
      axios.post(`${API}/admin/sheet-backfill-auto`, {}, headers(k)).catch(() => {});
      try {
        const [leads, contacts, waitlist, challenge, subscribers, clicks] = await Promise.all([
          axios.get(`${API}/leads`, headers(k)),
          axios.get(`${API}/contact`, headers(k)),
          axios.get(`${API}/waitlist`, headers(k)),
          axios.get(`${API}/challenge`, headers(k)),
          axios.get(`${API}/subscribers`, headers(k)),
          axios.get(`${API}/chalkit-clicks/stats`, headers(k)),
        ]);
        setData({
          leads: leads.data,
          contacts: contacts.data,
          waitlist: waitlist.data,
          challenge: challenge.data,
          subscribers: subscribers.data,
          clicks: clicks.data.products.map((p) => ({ ...p, id: p.product, label: PRODUCT_LABELS[p.product] || p.product })),
        });
      } catch (err) {
        if (err.response?.status === 401) {
          sessionStorage.removeItem("ig_admin_token");
          setAuthed(false);
          toast({ title: "Session expired", description: "Please enter the admin passcode again." });
        } else {
          toast({ title: "Couldn't load data", description: "Please try refreshing." });
        }
      } finally {
        setLoading(false);
      }
    },
    [headers, toast]
  );

  useEffect(() => {
    sessionStorage.removeItem("ig_admin_key");
    const saved = sessionStorage.getItem("ig_admin_token");
    if (saved) {
      setAuthed(true);
      fetchAll(saved);
    }
  }, [fetchAll]);

  const login = async (e) => {
    e.preventDefault();
    setChecking(true);
    try {
      let res;
      if (legacyMode) {
        res = await axios.post(`${API}/admin/session`, { key });
      } else {
        res = await axios.post(`${API}/admin/login`, { email: email.trim(), password });
      }
      sessionStorage.setItem("ig_admin_token", res.data.token);
      setAuthed(true);
      setPassword("");
      fetchAll(res.data.token);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Couldn't sign in. Check your details and try again.";
      if (err.response?.status === 429) {
        toast({ title: "Locked out", description: msg });
      } else if (err.response?.status === 401) {
        toast({ title: legacyMode ? "Wrong passcode" : "Sign-in failed", description: msg });
      } else {
        toast({ title: "Something went wrong", description: msg });
      }
    } finally {
      setChecking(false);
    }
  };

  const forgotPassword = async () => {
    const target = email.trim();
    if (!target) {
      toast({ title: "Enter your email first", description: "Type your email above, then tap Forgot password." });
      return;
    }
    try {
      const r = await axios.post(`${API}/admin/forgot-password`, { email: target });
      toast({ title: "Check your inbox", description: r.data.message });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast({ title: "Couldn't send reset", description: typeof detail === "string" ? detail : "Please try again shortly." });
    }
  };

  const logout = () => {
    sessionStorage.removeItem("ig_admin_token");
    setAuthed(false);
    setKey("");
    setData({ leads: [], contacts: [], waitlist: [], challenge: [], subscribers: [], clicks: [] });
  };

  const remove = async (type, id) => {
    const endpoint = type === "leads" ? "leads" : type === "contacts" ? "contact" : type === "challenge" ? "challenge" : type === "subscribers" ? "subscribers" : "waitlist";
    try {
      await axios.delete(`${API}/${endpoint}/${id}`, headers());
      setData((d) => ({ ...d, [type]: d[type].filter((x) => x.id !== id) }));
      toast({ title: "Deleted", description: "The entry has been removed." });
    } catch (err) {
      toast({ title: "Delete failed", description: "Couldn't remove that entry. Try again." });
    }
  };

  const [pinging, setPinging] = useState(false);
  const [mcSyncing, setMcSyncing] = useState(false);

  const LEAD_STATUSES = ["New", "Contacted", "Goal Review Booked", "Joined", "Lost"];

  const setLeadStatus = async (id, status) => {
    try {
      const r = await axios.patch(`${API}/leads/${id}`, { status }, headers());
      setData((d) => ({
        ...d,
        leads: d.leads.map((x) => (x.id === id ? { ...x, status, contacted_at: r.data.contacted_at || x.contacted_at } : x)),
      }));
    } catch (err) {
      toast({ title: "Couldn't update status", description: err?.response?.data?.detail || "Please try again." });
    }
  };

  const setLeadNotes = async (id, notes) => {
    try {
      await axios.patch(`${API}/leads/${id}`, { notes }, headers());
      setData((d) => ({ ...d, leads: d.leads.map((x) => (x.id === id ? { ...x, notes } : x)) }));
      toast({ title: "Note saved" });
    } catch (err) {
      toast({ title: "Couldn't save note", description: err?.response?.data?.detail || "Please try again." });
    }
  };

  const saveSettings = async (patch) => {
    try {
      const r = await axios.put(`${API}/admin/settings`, patch, headers());
      setSettings(r.data);
      toast({ title: "Settings saved" });
      return true;
    } catch (err) {
      toast({ title: "Couldn't save settings", description: err?.response?.data?.detail || "Please try again." });
      return false;
    }
  };

  const refresh = () => {
    fetchAll();
    setRefreshKey((k) => k + 1);
  };

  const [sheetSyncing, setSheetSyncing] = useState(false);
  const sheetBackfill = async () => {
    if (sheetSyncing) return;
    setSheetSyncing(true);
    try {
      const r = await axios.post(`${API}/admin/sheet-backfill`, {}, { ...headers(), timeout: 600000 });
      const c = r.data.counts || {};
      toast({
        title: "Google Sheet sync complete",
        description: `Posted ${r.data.posted}/${r.data.total} rows — leads ${c.leads}, contacts ${c.contact_messages}, waitlist ${c.waitlist}, challenge ${c.challenge_signups}, subscribers ${c.subscribers}${r.data.failed_batches ? ` · ${r.data.failed_batches} batches failed` : ""}.`,
      });
      fetchAll();
    } catch (err) {
      toast({ title: "Sheet sync failed", description: err?.response?.data?.detail || "Request timed out or errored — check server logs." });
    } finally {
      setSheetSyncing(false);
    }
  };

  const mcBackfill = async () => {
    if (mcSyncing) return;
    setMcSyncing(true);
    try {
      const r = await axios.post(`${API}/admin/mailchimp-backfill`, {}, { ...headers(), timeout: 600000 });
      toast({
        title: "Mailchimp sync complete",
        description: `Synced ${r.data.synced} · already subscribed ${r.data.already_subscribed} · failed ${r.data.failed}.`,
      });
      fetchAll();
    } catch (err) {
      toast({ title: "Mailchimp sync failed", description: err?.response?.data?.detail || "Request timed out or errored — check server logs." });
    } finally {
      setMcSyncing(false);
    }
  };
  const pingIndexNow = async () => {
    if (pinging) return;
    setPinging(true);
    try {
      const r = await axios.post(`${API}/admin/indexnow-ping`, {}, headers());
      const d = r.data || {};
      toast({
        title: d.ok ? "IndexNow ping sent" : "Ping failed",
        description: d.ok
          ? `${d.submitted} URLs submitted (HTTP ${d.status_code}). ${d.message || ""}`
          : d.message || "Please try again in a minute.",
      });
    } catch (err) {
      toast({ title: "Ping failed", description: err?.response?.data?.detail || err?.response?.data?.message || "Please try again in a minute." });
    } finally {
      setPinging(false);
    }
  };

  const resync = async (id) => {
    try {
      await axios.post(`${API}/leads/${id}/resync`, {}, headers());
      setData((d) => ({ ...d, leads: d.leads.map((x) => (x.id === id ? { ...x, chalkit_synced: true } : x)) }));
      toast({ title: "Sent to Chalk It Pro", description: "Zapier accepted the lead — it's synced now." });
    } catch (err) {
      toast({ title: "Resend failed", description: err?.response?.data?.detail || "Zapier rejected it again. Try later or add it manually." });
    }
  };

  const exportCsv = () => {
    const cfg = CSV_CONFIGS[tab];
    const rows = data[tab];
    if (!rows.length) {
      toast({ title: "Nothing to export", description: "This list is empty." });
      return;
    }
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [
      cfg.cols.map(([label]) => esc(label)).join(","),
      ...rows.map((r) => cfg.cols.map(([, key]) => esc(key === "created_at" || key === "last_click" ? fmtDate(r[key]) : r[key])).join(",")),
    ].join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `irongrit-${cfg.name}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
    toast({ title: "Export ready", description: `${rows.length} rows downloaded as CSV.` });
  };

  // ---------------- Login gate ----------------
  if (!authed) {
    return (
      <>
        <Seo title="Admin | Boston Iron Grit" description="Private admin dashboard." path="/admin" noindex />
        <section className="min-h-[80vh] bg-[#0A0A0B] flex items-center justify-center px-6">
          <div className="w-full max-w-sm bg-[#121214] border border-white/10 p-9">
            <div className="w-12 h-12 bg-[#9D0423]/12 border border-[#9D0423]/30 flex items-center justify-center">
              <Lock size={20} className="text-[#9D0423]" />
            </div>
            <h1 className="mt-6 font-heading font-extrabold uppercase text-2xl text-white tracking-tight">Coaches Only</h1>
            <p className="mt-2 text-[13px] text-neutral-500 leading-relaxed">
              {legacyMode ? "Enter the admin passcode to view the marketing dashboard." : "Sign in with your email and password to view the marketing dashboard."}
            </p>
            <form onSubmit={login} className="mt-6 space-y-4">
              {legacyMode ? (
                <input
                  type="password"
                  required
                  value={key}
                  onChange={(e) => setKey(e.target.value)}
                  placeholder="Admin passcode"
                  aria-label="Admin passcode"
                  data-testid="admin-passcode"
                  className="w-full bg-[#0D0D0F] border border-white/10 focus:border-[#9D0423] focus:outline-none focus:ring-1 focus:ring-[#9D0423]/40 text-white placeholder:text-neutral-600 px-4 py-3.5 text-sm transition-colors"
                />
              ) : (
                <>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email"
                    aria-label="Email"
                    autoComplete="username"
                    data-testid="admin-email"
                    className="w-full bg-[#0D0D0F] border border-white/10 focus:border-[#9D0423] focus:outline-none focus:ring-1 focus:ring-[#9D0423]/40 text-white placeholder:text-neutral-600 px-4 py-3.5 text-sm transition-colors"
                  />
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Password"
                    aria-label="Password"
                    autoComplete="current-password"
                    data-testid="admin-password"
                    className="w-full bg-[#0D0D0F] border border-white/10 focus:border-[#9D0423] focus:outline-none focus:ring-1 focus:ring-[#9D0423]/40 text-white placeholder:text-neutral-600 px-4 py-3.5 text-sm transition-colors"
                  />
                </>
              )}
              <button
                type="submit"
                disabled={checking}
                data-testid="admin-login"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#9D0423] hover:bg-[#7E031C] disabled:opacity-60 text-white font-mono font-semibold text-[12px] uppercase tracking-[0.14em] px-6 py-3.5 transition-colors"
              >
                {checking ? "Checking…" : "Unlock Dashboard"}
              </button>
            </form>
            <div className="mt-5 flex items-center justify-between">
              {!legacyMode ? (
                <button type="button" onClick={forgotPassword} data-testid="admin-forgot-password" className="font-mono text-[10px] uppercase tracking-[0.14em] text-neutral-500 hover:text-[#E56A79] transition-colors">
                  Forgot password?
                </button>
              ) : (
                <span />
              )}
              <button
                type="button"
                onClick={() => setLegacyMode(!legacyMode)}
                data-testid="admin-toggle-legacy"
                className="font-mono text-[10px] uppercase tracking-[0.14em] text-neutral-600 hover:text-white transition-colors"
              >
                {legacyMode ? "Use email login" : "Use legacy passcode"}
              </button>
            </div>
          </div>
        </section>
      </>
    );
  }

  // ---------------- Dashboard ----------------
  const isDataTab = TABS.some((t) => t.id === tab);
  const stats = [
    { label: "Goal Review Leads", value: data.leads.length, icon: Users },
    { label: "Contact Messages", value: data.contacts.length, icon: Mail },
    { label: "App Waitlist", value: data.waitlist.length, icon: Smartphone },
    { label: "Challenge Signups", value: data.challenge.length, icon: Flame },
    { label: "Subscribers", value: data.subscribers.length, icon: Newspaper },
    { label: "ChalkIt Clicks", value: data.clicks.reduce((n, c) => n + c.total, 0), icon: MousePointerClick },
  ];

  return (
    <>
      <Seo title="Marketing Dashboard | Boston Iron Grit Admin" description="Private admin dashboard." path="/admin" noindex />
      <section className="bg-[#0A0A0B] min-h-[85vh] py-14">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#9D0423] flex items-center gap-2">
                <ShieldCheck size={13} /> Coaches Only
              </p>
              <h1 className="mt-3 font-heading font-extrabold uppercase text-4xl text-white tracking-tight" data-testid="admin-title">Marketing Dashboard</h1>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setTab("pipeline")}
                data-testid="admin-jump-pipeline"
                className="inline-flex items-center gap-2 bg-[#9D0423] hover:bg-[#7E031C] text-white font-mono text-[11px] uppercase tracking-[0.14em] px-4 py-2.5 transition-colors"
              >
                <Columns3 size={13} /> Lead Pipeline
              </button>
              {isDataTab && (
                <button
                  onClick={exportCsv}
                  data-testid="admin-export-csv"
                  className="inline-flex items-center gap-2 border border-white/15 hover:border-white/40 text-neutral-300 hover:text-white font-mono text-[11px] uppercase tracking-[0.14em] px-4 py-2.5 transition-colors"
                >
                  <Download size={13} /> Export CSV
                </button>
              )}
              <button
                onClick={pingIndexNow}
                disabled={pinging}
                data-testid="admin-indexnow-ping"
                title="Notify Bing & partner search engines that pages changed (IndexNow)"
                className="inline-flex items-center gap-2 border border-white/15 hover:border-white/40 disabled:opacity-50 text-neutral-300 hover:text-white font-mono text-[11px] uppercase tracking-[0.14em] px-4 py-2.5 transition-colors"
              >
                <Radar size={13} className={pinging ? "animate-pulse" : ""} /> {pinging ? "Pinging…" : "Ping Bing"}
              </button>
              <button
                onClick={refresh}
                data-testid="admin-refresh"
                className="inline-flex items-center gap-2 border border-white/15 hover:border-white/40 text-neutral-300 hover:text-white font-mono text-[11px] uppercase tracking-[0.14em] px-4 py-2.5 transition-colors"
              >
                <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
              </button>
              <button
                onClick={logout}
                data-testid="admin-logout"
                className="inline-flex items-center gap-2 border border-white/15 hover:border-[#9D0423] text-neutral-300 hover:text-white font-mono text-[11px] uppercase tracking-[0.14em] px-4 py-2.5 transition-colors"
              >
                <LogOut size={13} /> Sign Out
              </button>
            </div>
          </div>

          {/* Marketing tabs + range */}
          <div className="mt-8 flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
            <div className="flex flex-wrap gap-2">
              {MARKETING_TABS.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  data-testid={`tab-${t.id}`}
                  className={`inline-flex items-center gap-2 px-4 py-3 font-mono text-[11px] uppercase tracking-[0.14em] border transition-all duration-200 ${
                    tab === t.id ? "bg-[#9D0423] border-[#9D0423] text-white" : "border-white/10 text-neutral-400 hover:border-white/30 hover:text-white"
                  }`}
                >
                  <t.icon size={13} /> {t.label}
                </button>
              ))}
            </div>
            <RangeToggle range={range} setRange={setRange} />
          </div>

          {/* Data tabs */}
          <div className="mt-3 flex flex-wrap gap-2">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`tab-${t.id}`}
                className={`inline-flex items-center gap-2 px-3.5 py-2 font-mono text-[10px] uppercase tracking-[0.12em] border transition-all duration-200 ${
                  tab === t.id ? "bg-white/10 border-white/30 text-white" : "border-white/5 text-neutral-500 hover:border-white/20 hover:text-white"
                }`}
              >
                <t.icon size={12} /> {t.label}
              </button>
            ))}
          </div>

          {/* Marketing dashboard tabs */}
          {tab === "overview" && <Overview headers={headers} range={range} refreshKey={refreshKey} toast={toast} />}
          {tab === "pipeline" && <Pipeline leads={data.leads} range={range} onStatus={setLeadStatus} onNotes={setLeadNotes} toast={toast} />}
          {tab === "seo" && (
            <SeoSearch headers={headers} range={range} refreshKey={refreshKey} settings={settings} saveSettings={saveSettings} onGoConnections={() => setTab("connections")} />
          )}
          {tab === "analytics" && <Analytics headers={headers} range={range} refreshKey={refreshKey} onGoConnections={() => setTab("connections")} />}
          {tab === "gbp" && <GbpReviews headers={headers} range={range} refreshKey={refreshKey} toast={toast} />}
          {tab === "connections" && (
            <Connections headers={headers} range={range} refreshKey={refreshKey} leads={data.leads} settings={settings} saveSettings={saveSettings} toast={toast} />
          )}

          {/* Stats — data tabs only */}
          {isDataTab && (
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-px bg-white/5 border border-white/5">
            {stats.map((s) => (
              <div key={s.label} className="bg-[#0D0D0F] p-6 flex items-center gap-4">
                <s.icon size={20} className="text-[#9D0423] shrink-0" />
                <div>
                  <div className="font-heading font-extrabold text-3xl text-white leading-none" data-testid={`stat-${s.label.split(" ")[0].toLowerCase()}`}>{s.value}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 mt-1.5">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
          )}

          {/* Booking funnel — goal review vs other booking clicks */}
          {tab === "clicks" && <BookingFunnel clicks={data.clicks} />}

          {/* Table */}
          {tab === "leads" && (
            <div className="mt-6 flex flex-wrap justify-end gap-3">
              <a
                href="https://docs.google.com/spreadsheets/d/10yqx1IIb30kBlEkg0YkGffCRHCGc5Pv4y0MOMHPOiFI/edit"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="open-google-sheet"
                className="inline-flex items-center gap-2 border border-white/15 hover:border-white/40 text-neutral-300 hover:text-white font-mono text-[11px] uppercase tracking-[0.14em] px-4 py-2.5 transition-colors"
              >
                <ExternalLink size={13} /> Open Google Sheet
              </a>
              <button
                onClick={sheetBackfill}
                disabled={sheetSyncing}
                data-testid="sheet-backfill-btn"
                title="Post every lead, contact, waitlist, challenge and subscriber record to the Google Sheet (deduped by id, no emails)"
                className="inline-flex items-center gap-2 border border-white/15 hover:border-white/40 disabled:opacity-50 text-neutral-300 hover:text-white font-mono text-[11px] uppercase tracking-[0.14em] px-4 py-2.5 transition-colors"
              >
                <FileSpreadsheet size={13} className={sheetSyncing ? "animate-pulse" : ""} /> {sheetSyncing ? "Sending…" : "Send all to Google Sheet"}
              </button>
              <button
                onClick={mcBackfill}
                disabled={mcSyncing}
                data-testid="mailchimp-backfill-btn"
                title="Send every lead, contact, waitlist, challenge and subscriber record to Mailchimp"
                className="inline-flex items-center gap-2 border border-white/15 hover:border-white/40 disabled:opacity-50 text-neutral-300 hover:text-white font-mono text-[11px] uppercase tracking-[0.14em] px-4 py-2.5 transition-colors"
              >
                <RefreshCw size={13} className={mcSyncing ? "animate-spin" : ""} /> {mcSyncing ? "Syncing…" : "Sync all to Mailchimp"}
              </button>
            </div>
          )}
          {isDataTab && (
          <div className="mt-6 border border-white/10 overflow-x-auto">
            {tab === "leads" && (
              <Table
                cols={["Name", "Contact", "Program", "Form / Source", "Status", "Slot", "Message", "Synced", "Mailchimp", "Received", ""]}
                rows={data.leads}
                empty="No leads yet."
                render={(l) => [
                  <span key="name" className="text-white font-semibold whitespace-nowrap">{l.name}</span>,
                  <span key="contact" className="block">
                    {l.email && <a href={`mailto:${l.email}`} className="block text-[#E56A79] hover:text-white transition-colors">{l.email}</a>}
                    {l.phone && <a href={`tel:${l.phone}`} className="block text-neutral-300 hover:text-white transition-colors">{l.phone}</a>}
                    {l.preferred_contact_method && <span className="block text-[10px] font-mono uppercase tracking-[0.1em] text-neutral-500">prefers {l.preferred_contact_method}</span>}
                  </span>,
                  <span key="program" className="inline-block font-mono text-[10px] uppercase tracking-[0.14em] text-neutral-300 border border-white/10 px-2 py-1 whitespace-nowrap">{l.program_interest}</span>,
                  <span key="form" className="block max-w-[140px]">
                    <span className="block text-neutral-300 truncate" title={l.form_name}>{l.form_name || "—"}</span>
                    <span className="block text-[10px] text-neutral-600 truncate">{[l.utm_source, l.utm_medium].filter(Boolean).join(" / ") || (l.gclid ? "google ads" : "direct")}</span>
                  </span>,
                  <span key="status" className="block whitespace-nowrap">
                    <select
                      value={l.status || "New"}
                      onChange={(e) => setLeadStatus(l.id, e.target.value)}
                      data-testid={`lead-status-${l.id}`}
                      className="bg-[#0b0b0c] border border-white/15 text-white text-[11px] px-2 py-1.5 focus:outline-none focus:border-[#9D0423]"
                    >
                      {LEAD_STATUSES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                    {(l.status || "New") === "New" && Date.now() - new Date(l.created_at).getTime() > 2 * 3600e3 && (
                      <span data-testid={`lead-notcontacted-${l.id}`} className="mt-1.5 block w-fit bg-[#9D0423] text-white font-mono text-[9px] uppercase tracking-[0.12em] px-1.5 py-0.5">
                        Not contacted
                      </span>
                    )}
                    {l.suspected_bot && (
                      <span data-testid={`lead-suspectedbot-${l.id}`} title="Submitted suspiciously fast — review before contacting" className="mt-1.5 block w-fit bg-amber-500/10 border border-amber-500/40 text-amber-400 font-mono text-[9px] uppercase tracking-[0.12em] px-1.5 py-0.5">
                        Suspected bot
                      </span>
                    )}
                    {l.contacted_at && (
                      <span className="mt-1 block font-mono text-[9px] text-neutral-600">contacted {fmtDate(l.contacted_at)}</span>
                    )}
                  </span>,
                  <span key="slot" className="text-neutral-300 whitespace-nowrap text-[12px]">{l.preferred_day ? `${l.preferred_day.slice(0, 3)} · ${l.preferred_time || "any"}` : l.preferred_time || "—"}</span>,
                  <span key="msg" className="text-neutral-400 block max-w-[180px] truncate" title={[l.primary_goal, l.experience_level, l.message].filter(Boolean).join(" · ")}>
                    {[l.primary_goal, l.experience_level, l.message].filter(Boolean).join(" · ") || "—"}
                  </span>,
                  <span key="sync" className="whitespace-nowrap">
                    {l.chalkit_synced ? (
                      <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-emerald-400">ChalkIt ✓</span>
                    ) : (
                      <button
                        data-testid={`resync-lead-${l.id}`}
                        onClick={() => resync(l.id)}
                        title="Resend this lead to Chalk It Pro via Zapier"
                        className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#E56A79] border border-[#9D0423]/40 hover:bg-[#9D0423] hover:text-white px-2 py-1 transition-colors"
                      >
                        Resend →
                      </button>
                    )}
                  </span>,
                  <span key="mc" className="whitespace-nowrap font-mono text-[10px] uppercase tracking-[0.12em]" title={l.mailchimp_msg || ""} data-testid={`lead-mc-${l.id}`}>
                    {l.mailchimp_status === "success" ? (
                      <span className="text-emerald-400">MC ✓</span>
                    ) : l.mailchimp_status ? (
                      <span className="text-[#E56A79]">MC ✗</span>
                    ) : (
                      <span className="text-neutral-600">—</span>
                    )}
                  </span>,
                  <span key="date" className="text-neutral-500 whitespace-nowrap">{fmtDate(l.created_at)}</span>,
                  <DeleteBtn key="del" onClick={() => remove("leads", l.id)} />,
                ]}
              />
            )}
            {tab === "contacts" && (
              <Table
                cols={["Name", "Email", "Phone", "Message", "Received", ""]}
                rows={data.contacts}
                empty="No contact messages yet."
                render={(m) => [
                  <span key="name" className="text-white font-semibold">{m.name}</span>,
                  <a key="email" href={`mailto:${m.email}`} className="text-[#E56A79] hover:text-white transition-colors">{m.email}</a>,
                  <span key="phone" className="text-neutral-300">{m.phone || "—"}</span>,
                  <span key="msg" className="text-neutral-400 block max-w-[340px] truncate" title={m.message}>{m.message}</span>,
                  <span key="date" className="text-neutral-500 whitespace-nowrap">{fmtDate(m.created_at)}</span>,
                  <DeleteBtn key="del" onClick={() => remove("contacts", m.id)} />,
                ]}
              />
            )}
            {tab === "waitlist" && (
              <Table
                cols={["Email", "Name", "Source", "Joined", ""]}
                rows={data.waitlist}
                empty="No waitlist signups yet."
                render={(w) => [
                  <a key="email" href={`mailto:${w.email}`} className="text-[#E56A79] hover:text-white transition-colors">{w.email}</a>,
                  <span key="name" className="text-neutral-300">{w.name || "—"}</span>,
                  <span key="source" className="inline-block font-mono text-[10px] uppercase tracking-[0.14em] text-neutral-300 border border-white/10 px-2 py-1">{w.source}</span>,
                  <span key="date" className="text-neutral-500 whitespace-nowrap">{fmtDate(w.created_at)}</span>,
                  <DeleteBtn key="del" onClick={() => remove("waitlist", w.id)} />,
                ]}
              />
            )}
            {tab === "challenge" && (
              <Table
                cols={["Name", "Email", "Phone", "Joined", ""]}
                rows={data.challenge}
                empty="No challenge signups yet."
                render={(c) => [
                  <span key="name" className="text-white font-semibold">{c.name}</span>,
                  <a key="email" href={`mailto:${c.email}`} className="text-[#E56A79] hover:text-white transition-colors">{c.email}</a>,
                  <span key="phone" className="text-neutral-300">{c.phone || "—"}</span>,
                  <span key="date" className="text-neutral-500 whitespace-nowrap">{fmtDate(c.created_at)}</span>,
                  <DeleteBtn key="del" onClick={() => remove("challenge", c.id)} />,
                ]}
              />
            )}
            {tab === "subscribers" && (
              <Table
                cols={["Email", "Name", "Source", "Status", "Tags", "Joined", ""]}
                rows={data.subscribers}
                empty="No Grit Report subscribers yet."
                render={(s) => [
                  <a key="email" href={`mailto:${s.email}`} className="text-[#E56A79] hover:text-white transition-colors">{s.email}</a>,
                  <span key="name" className="text-neutral-300">{[s.first_name, s.last_name].filter(Boolean).join(" ") || "—"}</span>,
                  <span key="source" className="inline-block font-mono text-[10px] uppercase tracking-[0.14em] text-neutral-300 border border-white/10 px-2 py-1">{s.source}</span>,
                  <span key="status" className={`inline-block font-mono text-[10px] uppercase tracking-[0.14em] px-2 py-1 border ${s.status === "confirmed" ? "text-emerald-400 border-emerald-400/30" : s.status === "unsubscribed" ? "text-neutral-500 border-white/10" : "text-[#E8C766] border-[#E8C766]/30"}`}>{s.status}</span>,
                  <span key="tags" className="text-neutral-400 text-xs">{(s.tags || []).join(", ") || "—"}</span>,
                  <span key="date" className="text-neutral-500 whitespace-nowrap">{fmtDate(s.created_at)}</span>,
                  <DeleteBtn key="del" onClick={() => remove("subscribers", s.id)} />,
                ]}
              />
            )}
            {tab === "clicks" && (
              <Table
                cols={["Product", "Last 7 Days", "Last 30 Days", "Total Clicks", "Last Click"]}
                rows={data.clicks}
                empty="No ChalkIt clicks tracked yet — every tap on a ChalkIt Pro button across the site will show up here."
                render={(c) => [
                  <span key="label" className="text-white font-semibold">{c.label}</span>,
                  <span key="l7" className="text-neutral-300">{c.last7}</span>,
                  <span key="l30" className="text-neutral-300">{c.last30}</span>,
                  <span key="total" className="font-heading font-extrabold text-lg text-[#E56A79]">{c.total}</span>,
                  <span key="date" className="text-neutral-500 whitespace-nowrap">{fmtDate(c.last_click)}</span>,
                ]}
              />
            )}
          </div>
          )}

          <p className="mt-5 text-[11px] text-neutral-600">
            Passcode is set in the backend .env file (ADMIN_KEY). This page is hidden from search engines.
          </p>
        </div>
      </section>
    </>
  );
}

function BookingFunnel({ clicks }) {
  const periods = [
    { label: "Last 7 Days", key: "last7" },
    { label: "Last 30 Days", key: "last30" },
    { label: "All Time", key: "total" },
  ];
  const sum = (key, match) => clicks.filter(match).reduce((n, c) => n + (c[key] || 0), 0);
  return (
    <div className="mt-6 border border-white/10 bg-[#0D0D0F] p-6" data-testid="booking-funnel">
      <h3 className="font-heading font-extrabold uppercase text-lg text-white tracking-tight">Booking Funnel</h3>
      <p className="mt-1 text-[12px] text-neutral-500">Free Goal Review clicks vs all other ChalkIt Pro booking clicks (memberships, drop-ins, member reserves).</p>
      <div className="mt-5 grid sm:grid-cols-3 gap-px bg-white/5 border border-white/5">
        {periods.map(({ label, key }) => {
          const gr = sum(key, (c) => c.id === "goalReview");
          const other = sum(key, (c) => c.id !== "goalReview");
          const total = gr + other;
          const pct = total ? Math.round((gr / total) * 100) : 0;
          return (
            <div key={key} className="bg-[#121214] p-5" data-testid={`funnel-${key}`}>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">{label}</div>
              <div className="mt-3 flex items-baseline gap-5">
                <div>
                  <span className="font-heading font-extrabold text-2xl text-[#E56A79]" data-testid={`funnel-${key}-goal-review`}>{gr}</span>
                  <span className="ml-1.5 font-mono text-[10px] uppercase tracking-[0.12em] text-neutral-500">Goal Review</span>
                </div>
                <div>
                  <span className="font-heading font-extrabold text-2xl text-white" data-testid={`funnel-${key}-other`}>{other}</span>
                  <span className="ml-1.5 font-mono text-[10px] uppercase tracking-[0.12em] text-neutral-500">Other</span>
                </div>
              </div>
              <div className="mt-3 h-2 bg-white/10 overflow-hidden" aria-hidden="true">
                <div className="h-full bg-[#9D0423] transition-[width] duration-500" style={{ width: `${pct}%` }} />
              </div>
              <div className="mt-2 text-[11px] text-neutral-400" data-testid={`funnel-${key}-share`}>
                {total ? `${pct}% of booking clicks were Goal Review` : "No clicks yet"}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DeleteBtn({ onClick }) {
  return (
    <button
      onClick={onClick}
      aria-label="Delete entry"
      data-testid="admin-delete-row-button"
      className="p-2 text-neutral-600 hover:text-[#9D0423] transition-colors"
    >
      <Trash2 size={15} />
    </button>
  );
}

function Table({ cols, rows, render, empty }) {
  if (!rows.length) {
    return <div className="p-10 text-center text-neutral-500 text-sm bg-[#0D0D0F]">{empty}</div>;
  }
  return (
    <table className="w-full text-left text-sm">
      <thead>
        <tr className="bg-[#141416]">
          {cols.map((c, i) => (
            <th key={c || `col-${i}`} className="px-5 py-3.5 font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-400 border-b border-white/10 whitespace-nowrap">{c}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id} className="border-b border-white/5 last:border-0 bg-[#0D0D0F] hover:bg-[#121214] transition-colors">
            {render(r).map((cell, i) => (
              <td key={`${r.id}-${cols[i] || i}`} className="px-5 py-4 align-middle">{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
